import React from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { useNavigate, useParams } from 'react-router-dom'

import InvoiceForm from '../../components/invoices/form'
import { BusinessDetailsDraft } from '../../components/business/modal'
import EditPagePostHydrationModals from './EditPagePostHydrationModals'
import { getInvoiceById, removeInvoice } from '../../api'
import { Invoice } from '../../types'
import { useAlerts } from '../../context/alerts'
import { usePageHeader } from '../../context/pageHeader'
import InvoiceHistory from '../../components/invoices/history'
import Spinner from '../../components/ui/spinner'

const defaultValues: Invoice = {
  code: '',
  clientId: '',
  total: '',
  items: [],
  issueDate: new Date(),
  dueDate: new Date(new Date().setDate(new Date().getDate() + 7)),
  locked: false,
}

const EditInvoicePage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { addAlert } = useAlerts()
  const { setPageHeader, clearPageHeader } = usePageHeader()
  const { data, isLoading, error, mutate } = useSWR(`/api/invoices/${id}`, getInvoiceById)
  const { trigger: triggerRemoveInvoice, isMutating: isRemoving } = useSWRMutation(`/api/invoices/${id}`, removeInvoice)

  React.useEffect(() => {
    setPageHeader({
      title: 'Invoice',
      backTo: '/dashboard',
    })
    return () => clearPageHeader()
  }, [setPageHeader, clearPageHeader])

  const handleRemoveInvoice = React.useCallback(async () => {
    if(window.confirm('Are you sure you want to delete this invoice?')){
      await triggerRemoveInvoice()
      addAlert({ type: 'info', message: 'Invoice deleted' })
      navigate('/dashboard', { replace: true })
    }
  }, [triggerRemoveInvoice, addAlert, navigate])

  const handleSaveSuccess = React.useCallback(async () => {
    await mutate()
  }, [mutate])

  const [externalBusinessDetailsApply, setExternalBusinessDetailsApply] = React.useState<
    { nonce: number; draft: BusinessDetailsDraft } | undefined
  >()

  if(data?.isDeleted) {
    return <div>This invoice has been deleted</div>
  }

  if(error){
    return <div>Error ocurred.</div>
  }
  if(id === undefined) return <div>Invalid invoice id</div>
  if(isLoading) return <Spinner />

  return (
    <>
      <InvoiceForm
        key={id}
        defaultValues={data || defaultValues}
        onSuccess={handleSaveSuccess}
        triggerRemoveInvoice={handleRemoveInvoice}
        isDeleting={isRemoving}
        externalBusinessDetailsApply={externalBusinessDetailsApply}
      />
      <EditPagePostHydrationModals
        invoiceId={id}
        onBusinessDetailsApply={setExternalBusinessDetailsApply}
      />
      <div className='mt-5 pb-5'>
        <InvoiceHistory key={id} invoiceId={id} />
      </div>
    </>
  )
}

export default EditInvoicePage
